import React, { useState } from 'react';
import Axios from 'axios';
import EntityManagement from './entityManagement';
import { Entity } from './entityForm';
import { mergeStateValue, deepClone } from '../utilities/generic';
import { FilterSelections } from '../reducers/filterSelections';
import { Groups } from '../reducers/authenticated/authenticatedReducer';
import { useTranslation } from "react-i18next";

function CityManagement(props: Readonly<{
    filterSelections: FilterSelections,
    groups: Groups,
    onDataUpdated: () => void,
}>) {

    interface CityModel extends Entity {
        country_id: number;
        country_name: string;
    }

    const [countries, setCountries] = useState([]);
    const [cities, setCities] = useState([] as CityModel[]);
    const [currentCity, setCurrentCity] = useState<CityModel>({ country_id: 0, country_name: '', id: 0, name: '' });

    const restPath = '/v10/cities/';

    const { t } = useTranslation();

    function fetchData() {
        Axios.get('/v10/countries').then(response => setCountries(response.data.result));
        Axios.get('/v10/cities').then(response => setCities(response.data.result));
    }

    async function startEditing(id) {
        if (id === undefined) {
            setCurrentCity({ country_id: 0, country_name: '', id: 0, name: '' });
        } else {
            const city = cities.find(e => e.id === id);
            if (!city) {
                throw new Error('Internal error');
            }
            setCurrentCity(deepClone(city));
        }
    }

    function changeValue(name, value) {
        mergeStateValue(setCurrentCity, name, value);
    }

    function validate() {
        if (currentCity?.name?.trim() === "") {
            return t("Please specify a city name");
        } else if (!currentCity?.country_id) {
            return t("Please specify a country");
        }
        return "";
    }

    function getSanitizedEditingValues() {
        return { country_id: currentCity.country_id, name: currentCity?.name?.trim() };
    }

    return (
        <EntityManagement
            groups={props.groups}
            filterSelections={props.filterSelections}
            onDataUpdated={props.onDataUpdated}

            restPath={restPath}
            fetchData={fetchData}

            editor={{
                edit: startEditing,
                getSanitizedValues: getSanitizedEditingValues,
                set: changeValue,
                validate: validate,
                entityName: 'currentCity'
            }}

            texts={{
                localizedAddNewText: t('Add new city'),
                confirmDelete: t('Are you sure you want to delete the city?'),
                errors: {
                    generic: t("City couldn't be saved! Please try again or contact administrator!"),
                    alreadyExists: t("A City with that name already exists for the given country"),
                    deleteInUse: t("City is in use and cannot be deleted"),
                    deleteGeneric: t("City couldn't be deleted! Please try again or contact administrator!")
                }
            }}

            entities={{ cities: cities, currentCity: currentCity, countries: countries }}
            entityName='cities'

            tableHeaders={[{ localizedText: t('Country'), name: 'country_name', sortable: true },
            { localizedText: t('City'), name: 'name', sortable: true },
            { localizedText: t('Edit'), type: 'edit' },
            { localizedText: t('Delete'), type: 'delete' }]}

            formFields={[{ label: t('Country'), name: 'country_id', allowedValues: { entityName: 'countries', name: 'name' } },
            { label: t('City Name'), name: 'name', placeholder: t('Enter a city name'), maxLength: 255 }]}
        />
    );
}

export default CityManagement;
